import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { AD_FORMATS, adsConfig, adsRuntimeEnabled, canShowAd, getScrollDepth, normalizeRoute } from "./ads";

const ADSENSE_CLIENT = "ca-pub-6796341181529858";

const ADSENSE_PLACEMENTS = {
  top: {
    slot: "8779773604",
    name: "1 - Display - Article Top",
    format: "auto",
    fullWidthResponsive: "true",
    minHeightClass: "min-h-[100px] sm:min-h-[120px]"
  },
  article: {
    slot: "7466691933",
    name: "2 - In-article",
    format: "fluid",
    layout: "in-article",
    textAlign: "center",
    minHeightClass: "min-h-[120px]"
  },
  bottom: {
    slot: "8137197230",
    name: "3 - Multiplex - Article End",
    format: "autorelaxed",
    minHeightClass: "min-h-[220px]"
  },
  feed: {
    slot: "7709349064",
    name: "feed - In-feed - Homepage",
    format: "fluid",
    layoutKey: "-fb+5w+4e-db+86",
    minHeightClass: "min-h-[180px]"
  },
  sidebar: {
    slot: "6249608952",
    name: "ads - Display - Sidebar Desktop",
    format: "auto",
    fullWidthResponsive: "true",
    minHeightClass: "min-h-[280px]"
  }
};

function placementConfig(placement) {
  return ADSENSE_PLACEMENTS[placement] || ADSENSE_PLACEMENTS.article;
}

function formatForPlacement(placement) {
  if (placement === "sidebar") return AD_FORMATS.sidebarNative;
  return AD_FORMATS.articleNative;
}

export default function AdSlot({ placement = "article", articleSlug, className = "" }) {
  const location = useLocation();
  const route = useMemo(() => normalizeRoute(location.pathname), [location.pathname]);
  const slotRef = useRef(null);
  const adsenseRef = useRef(null);
  const adsenseInitializedRef = useRef(false);
  const [isVisible, setIsVisible] = useState(false);
  const config = placementConfig(placement);
  const slotFormat = formatForPlacement(placement);
  const slotInstanceKey = `${route}:${placement}:${config.slot}:${articleSlug || "page"}`;

  const [hasHydrated, setHasHydrated] = useState(false);
  useEffect(() => { setHasHydrated(true); }, []);

  const canRenderSlot = hasHydrated && adsRuntimeEnabled() && canShowAd(slotFormat, route, {
    scrollDepth: Math.max(getScrollDepth(), 10),
    timeOnSiteMs: adsConfig.delays.inPagePushMinMs
  });

  useEffect(() => {
    adsenseInitializedRef.current = false;
    setIsVisible(false);
  }, [slotInstanceKey]);

  useEffect(() => {
    if (!canRenderSlot || !slotRef.current) return undefined;

    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver((entries) => {
      const visibleEntry = entries.find((entry) => entry.isIntersecting);
      if (visibleEntry) {
        setIsVisible(true);
        observer.disconnect();
      }
    }, { rootMargin: "220px 0px", threshold: 0.05 });

    observer.observe(slotRef.current);
    return () => observer.disconnect();
  }, [canRenderSlot, slotInstanceKey]);

  useEffect(() => {
    if (!isVisible || !canRenderSlot || !adsenseRef.current || typeof window === "undefined") return;

    const adElement = adsenseRef.current;
    if (adElement.getAttribute("data-adsbygoogle-status")) {
      adsenseInitializedRef.current = true;
      return;
    }

    if (adsenseInitializedRef.current) return;

    try {
      window.adsbygoogle = window.adsbygoogle || [];
      window.adsbygoogle.push({});
      adsenseInitializedRef.current = true;
    } catch (error) {
      if (adsConfig.debug && typeof console !== "undefined") {
        console.warn(`[TechniaHQRobot Ads] ${config.name} could not initialize.`, error);
      }
    }
  }, [canRenderSlot, config.name, isVisible, slotInstanceKey]);

  if (!canRenderSlot) return null;

  const adDataAttributes = {
    "data-ad-client": ADSENSE_CLIENT,
    "data-ad-slot": config.slot,
    "data-ad-format": config.format
  };

  if (config.layout) adDataAttributes["data-ad-layout"] = config.layout;
  if (config.layoutKey) adDataAttributes["data-ad-layout-key"] = config.layoutKey;
  if (config.fullWidthResponsive) adDataAttributes["data-full-width-responsive"] = config.fullWidthResponsive;

  return (
    <aside
      ref={slotRef}
      className={`w-full max-w-full overflow-hidden rounded-[1.5rem] border border-neutral-200 bg-neutral-50 p-4 sm:p-5 ${className}`}
      aria-label="Advertisement"
      data-ad-placement={placement}
      data-ad-format={config.format}
      data-ad-article={articleSlug || route}
      data-adsense-slot={config.slot}
      data-adsense-unit={config.name}
      data-sponsored-ad="visible-placement"
    >
      <p className="text-[0.68rem] font-black uppercase tracking-[0.18em] text-neutral-500">Advertisement</p>
      <div className={`mt-3 w-full max-w-full overflow-hidden ${config.minHeightClass}`}>
        <ins
          key={slotInstanceKey}
          ref={adsenseRef}
          className="adsbygoogle"
          style={{ display: "block", ...(config.textAlign ? { textAlign: config.textAlign } : {}) }}
          {...adDataAttributes}
        />
      </div>
    </aside>
  );
}
